import { useEffect, useState } from 'react';
import { Gift, Copy, Check, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type Stats = { code: string; referred: number; months: number; pending: number; pro_until: string | null };

export const referralLink = (code: string) => `https://sportaraapp.com/coaches?ref=${code}`;
export const referralMessage = (code: string) =>
  `I use Sportara to manage my team's sports education — it's free to start. Check it out: ${referralLink(code)}`;

/** Owner-only referral widget: link, share message, and real credited counts. */
export function ReferralCard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    supabase.rpc('get_my_referral_stats' as any).then(({ data, error: e }) => {
      if (e || !data) setError(true); else setStats(data as unknown as Stats);
    });
  }, []);

  const copyMessage = async () => {
    if (!stats) return;
    try {
      await navigator.clipboard.writeText(referralMessage(stats.code));
      setCopied(true); toast.success('Share message copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy — select the message and copy it manually.'); }
  };
  const share = async () => {
    if (!stats) return;
    if (navigator.share) {
      try { await navigator.share({ text: referralMessage(stats.code) }); return; } catch { return; }
    }
    copyMessage();
  };

  if (error) return <p className="text-xs text-muted-foreground mb-4">Referral details couldn't load. Refresh to try again.</p>;
  if (!stats) return null;
  const n = stats.referred;
  const until = stats.pro_until && new Date(stats.pro_until) > new Date() ? new Date(stats.pro_until).toLocaleDateString() : null;

  return (
    <section id="refer-a-coach" aria-labelledby="refer-heading" className="mb-6 rounded-2xl border border-border bg-card p-4 scroll-mt-24">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-primary" /></div>
        <div className="min-w-0 flex-1">
          <h3 id="refer-heading" className="font-display font-bold text-foreground">Refer a coach</h3>
          <p className="text-sm text-foreground mt-0.5">
            You've referred {n} {n === 1 ? 'coach' : 'coaches'} — earned {stats.months} free {stats.months === 1 ? 'month' : 'months'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Earn 1 free month of Coach Pro for each new coach who signs up with your link, confirms their email and creates a team.
            {stats.pending > 0 && ` ${stats.pending} pending.`}
            {until && ` Free Pro active until ${until}.`}
          </p>
        </div>
      </div>
      <p className="mt-3 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground break-words">{referralMessage(stats.code)}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={copyMessage} className="font-display font-bold">
          {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />} Copy message
        </Button>
        <Button size="sm" variant="outline" onClick={share} className="font-display font-bold sm:hidden">
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
        </Button>
      </div>
    </section>
  );
}
